import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Chip } from "@/shared/components/ui-kit";
import { ArgsTable } from "./ArgsTable";

import type { AppToolSpec, ToolRisk } from "@/shared/types";

/* Seed-time heuristic, not a server-side gate (backend/modules/apps/README.md) — so it is
   shown as a label and never as a promise. Read is the quiet default; write and
   danger earn a tone because they are what an approval policy will care about. */
const RISK_TONE: Record<ToolRisk, React.ComponentProps<typeof Chip>["tone"]> = {
  read: "neutral",
  write: "warn",
  danger: "danger",
};

export function ToolRow({ tool }: { tool: AppToolSpec }) {
  const [open, setOpen] = useState(false);
  const required = tool.args.filter((a) => a.req).length;
  return (
    <div className="border-t hairline first:border-t-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full press flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--accent-hover)]"
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" strokeWidth={1.75} />
          : <ChevronRight className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" strokeWidth={1.75} />}
        <div className="min-w-0 flex-1">
          {/* The slug drops under the name when both do not fit, rather than truncating to a few letters */}
          <div className="flex flex-wrap items-baseline gap-x-2 min-w-0">
            <span className="min-w-0 break-words text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
              {tool.name}
            </span>
            <code className="min-w-0 max-w-full text-[11px] text-[var(--text-quaternary)] truncate">{tool.slug}</code>
          </div>
          <div className="text-[12.5px] text-[var(--text-secondary)] mt-[1px] truncate">{tool.desc}</div>
        </div>
        <Chip tone={RISK_TONE[tool.risk]}>{tool.risk}</Chip>
        <span className="shrink-0 whitespace-nowrap text-[11.5px] text-[var(--text-tertiary)] tabular-nums">
          {tool.args.length} args
          {/* the asterisks in the expanded table say which on a phone */}
          {required > 0 && <span className="hidden sm:inline"> · {required} required</span>}
        </span>
      </button>
      {open && (
        <div className="fade-in">
          <ArgsTable args={tool.args} />
          <div className="px-4 pb-3.5 pt-1">
            <div className="section-label pb-1.5">Returns</div>
            <div className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">{tool.returns}</div>
          </div>
        </div>
      )}
    </div>
  );
}
